// ============================================================
// shared/brut.js — la forme du brut est une RÈGLE, pas une devinette.
// ------------------------------------------------------------
// L'agent 1 rend la forme de la pièce FINIE : c'est de la perception,
// c'est ce qui est dessiné. Le brut, lui, se DÉDUIT — et une règle est
// juste 100 % du temps, s'explique en une phrase, et ne coûte rien.
//
// Ce n'est pas une économie de jetons, c'est une question de justesse.
// Le bug qui a motivé cette refonte : sur une rondelle Ø75 × 7, le
// modèle a rendu « brut prismatique » puis n'a pu remplir ni longueur,
// ni largeur, ni épaisseur — trois cotes qui n'existent pas sur un
// disque. On lui demandait quelque chose qu'il ne pouvait pas savoir.
//
// **On ne demande pas à un modèle ce qu'une règle fait parfaitement.**
// L'intelligence se garde pour là où il n'y a pas de règle : les prises
// et les temps.
//
// ── Les règles ──
//
//   rotative                        → barre ronde
//   plate / disque                  → barre ronde, ou tôle si le Ø
//                                     dépasse le seuil d'atelier
//   prismatique                     → barreau
//   rotative avec méplats ou
//   perçages hors axe               → barre ronde reprise en prismatique
//
// Le seuil « disque au-delà de tant : tôle plutôt que barre » est un
// RÉGLAGE d'atelier, pas une décision par pièce : découper un disque de
// Ø300 dans une barre coûte une fortune en copeaux, mais en dessous
// d'un certain diamètre la barre est plus simple à tenir. Charles le
// règle une fois.
// ============================================================

import { envelopeVolume } from './geometry.js';

/**
 * Les QUATRE formes de brut. Un seul vocabulaire, partagé avec
 * `shared/pricing.js` (`RAW_SHAPES`) et la colonne `pieces.raw_shape`.
 *
 * `a_la_cote` est la quatrième, ajoutée au RDV du 5 août : le patron l'a
 * citée deux fois — « tu vas recevoir une plaque ébauchée à 5 mm », « de
 * l'étiré 40×10 ». C'est le cas où une surépaisseur forfaitaire est le
 * plus fausse, et il est courant en sous-traitance.
 *
 * L'ordre compte : c'est celui des boutons de l'écran, et il va du plus
 * fréquent au plus particulier.
 */
export const BRUT_SHAPES = ['barreau', 'barre_ronde', 'barre_ronde_prismatique', 'a_la_cote'];

/** Les libellés, UNE seule fois — §3.7, un seul contrôle, un seul mot. */
export const BRUT_LABELS = {
  barreau: 'Barreau prismatique',
  barre_ronde: 'Barre ronde',
  barre_ronde_prismatique: 'Barre ronde reprise en prismatique',
  a_la_cote: 'Reçu à la cote (plaque, étiré)',
};

/**
 * Ce que chaque forme change, en une phrase, pour l'écran.
 *
 * ── Pourquoi `barre_ronde_prismatique` a besoin de cette phrase ──
 *
 * Elle rend EXACTEMENT les mêmes cotes que `barre_ronde`, sur toutes les
 * géométries. Ce n'est pas un calcul manquant : **on achète la même
 * barre.** Ce qui les distingue n'est pas le brut, c'est la GAMME — la
 * seconde impose une reprise sur un poste prismatique, donc un montage
 * de plus, donc un prix plus élevé.
 *
 * Sans cette phrase, l'écran propose un choix qui ne change aucun
 * chiffre du bloc où il se trouve : Charles clique, rien ne bouge, et
 * il n'a aucun moyen de savoir pourquoi. Un écran qui affirme une
 * distinction doit dire OÙ elle porte.
 */
export const BRUT_EFFETS = {
  barreau: 'Débité dans une plaque ou un plat.',
  barre_ronde: 'Débité dans une barre ronde, usiné au tour.',
  barre_ronde_prismatique:
    'Même barre et mêmes cotes que « barre ronde » — ce qui change est la GAMME : '
    + 'une reprise sur un poste prismatique, donc un montage de plus.',
  a_la_cote: 'Reçu aux cotes finies : aucune surépaisseur, aucune montée en taille commerciale.',
};

/** Les formes qui se cotent en Ø × longueur, et non en L × l × h. */
export const FORMES_RONDES = ['barre_ronde', 'barre_ronde_prismatique'];

/**
 * Surépaisseurs d'usinage par défaut, en millimètres.
 *
 * Valeurs d'atelier courantes, à confirmer par Charles. Elles vivent
 * dans `app_settings.pricing` et pas en dur : chez AGC elles étaient
 * codées, et la profondeur produisait des cotes non commerciales comme
 * 12,1 mm.
 *
 *  - `rayon` : 2 mm AU RAYON en tournage, soit 4 mm sur le diamètre.
 *  - `face`  : 2 mm par face en fraisage.
 *  - `longueur` : 4 mm en longueur, pour la reprise et le tronçonnage.
 */
export const SUREPAISSEURS = {
  tournage_rayon_mm: 2,
  fraisage_face_mm: 2,
  /** Dressage : 1 mm PAR FACE, appliqué deux fois sur la longueur. */
  dressage_face_mm: 1,
  /** Trait de scie : la matière qu'emporte le débit, perdue à coup sûr. */
  trait_de_scie_mm: 3,
  longueur_mm: 4,
  /**
   * Au-delà, un disque se débite dans une tôle plutôt que dans une barre.
   *
   * **200 mm est une valeur de DÉPART, à confirmer avec Charles** — au
   * même titre que les prix au kilo, et pour la même raison : c'est un
   * arbitrage d'atelier, pas une constante.
   *
   * Ce qui le rend discutable : Mertens n'a **ni laser ni jet d'eau**.
   * Un disque découpé dans de la tôle se détoure donc sur un centre
   * 3 axes, et l'économie de matière se paie en temps machine — au
   * taux machine, sur un contour qui peut être long. L'arbitrage
   * dépend aussi de si Charles achète des disques prédécoupés.
   *
   * C'est typiquement une **candidate à devenir une règle métier née de
   * sa bouche** : « au-delà de tant, je prends du prédécoupé » vaut
   * mieux qu'un nombre choisi ici.
   */
  disque_seuil_diametre_mm: 200,
  disque_seuil_confirme: false,
};

/**
 * Déduit la forme du brut de la forme de la pièce finie.
 *
 * @param {string} formePiece   `rotative` | `plate_disque` | `prismatique` | `complexe`
 * @param {object} [ctx]
 * @param {Array}  [ctx.entites]  les entités lues — servent à repérer
 *   les méplats et les perçages hors axe, qui imposent une reprise.
 * @param {number} [ctx.diametre_mm]
 * @param {object} [ctx.settings]
 * @returns {{shape:string, raison:string, alternatives:string[]}}
 */
export function deduireFormeBrut(formePiece, ctx = {}) {
  const s = { ...SUREPAISSEURS, ...(ctx.settings ?? {}) };
  const entites = ctx.entites ?? [];

  // Une entité hors axe de révolution impose une reprise sur un poste
  // prismatique : le brut reste rond, mais la pièce ne se fait pas
  // qu'au tour.
  const horsAxe = entites.some(
    (e) =>
      ['rainure', 'poche', 'fente', 'surfacage'].includes(e.type) ||
      (['percage', 'percage_etage', 'taraudage'].includes(e.type) &&
        e.groupe_face === 'pourtour'),
  );

  // ── LE SEUIL DE CHARLES VAUT POUR TOUTE PIÈCE DE RÉVOLUTION PLUS LARGE
  //    QUE LONGUE — 6 octobre 2026 ──
  //
  // Au-delà de 200 mm de Ø, une pièce courte se débite dans une tôle : dans
  // une barre, elle partirait presque entièrement en copeaux. La règle ne
  // visait que `plate_disque`. Depuis que la forme se juge au contour
  // extérieur (lecteur d'enveloppe v2), la bride ONERA Ø305 × 126 est lue
  // « rotative » — et elle partait en barre ronde Ø310. Une régression : la
  // règle porte sur la PROPORTION, pas sur le mot qui décrit la pièce.
  const diametreRond = Number(ctx.diametre_mm) > 0 ? Number(ctx.diametre_mm) : Number(ctx.diametre_global_mm);
  const longueurRonde = Number(ctx.longueur_mm);
  const largeEtCourte = (formePiece === 'rotative' || Number(ctx.diametre_global_mm) > 0)
    && diametreRond > s.disque_seuil_diametre_mm
    && longueurRonde > 0 && diametreRond > longueurRonde;
  if (largeEtCourte) {
    return {
      shape: 'barreau',
      par_defaut: false,
      raison:
        `Pièce de révolution de Ø${diametreRond} mm sur ${longueurRonde} mm de long, au-delà du `
        + `seuil d'atelier de ${s.disque_seuil_diametre_mm} mm : débitée dans une tôle plutôt que `
        + 'dans une barre, où elle partirait presque entièrement en copeaux.',
      alternatives: ['barre_ronde', 'barre_ronde_prismatique'],
    };
  }

  if (formePiece === 'rotative') {
    return horsAxe
      ? {
          shape: 'barre_ronde_prismatique',
          par_defaut: false,
          raison:
            'Pièce rotative portant des entités hors axe (rainure, méplat ou perçage sur le ' +
            'pourtour) : barre ronde, puis reprise sur un poste prismatique.',
          alternatives: ['barre_ronde'],
        }
      : {
          shape: 'barre_ronde',
          par_defaut: false,
          raison: 'Pièce de révolution : elle se débite dans une barre ronde.',
          alternatives: ['barre_ronde_prismatique'],
        };
  }

  if (formePiece === 'plate_disque') {
    // `Number(x) || 0` : un Ø absent devenait 0, donc « sous le seuil »,
    // donc barre ronde — une DÉCISION prise sur une donnée qu'on n'a
    // pas. Le seuil ne se compare que quand il y a quelque chose à
    // comparer.
    const diametre = Number.isFinite(Number(ctx.diametre_mm)) ? Number(ctx.diametre_mm) : null;
    if (diametre == null) {
      return {
        shape: 'barre_ronde',
        par_defaut: true,
        raison:
          'Disque dont le Ø n’a pas été relevé : barre ronde proposée par défaut. '
          + `Au-delà de ${s.disque_seuil_diametre_mm} mm, une tôle est plus économique — `
          + 'renseignez le Ø pour que la règle tranche.',
        alternatives: BRUT_SHAPES.filter((f) => f !== 'barre_ronde'),
      };
    }
    if (diametre > s.disque_seuil_diametre_mm) {
      return {
        shape: 'barreau',
        par_defaut: false,
        raison:
          `Disque de Ø${diametre} mm, au-delà du seuil d'atelier de ` +
          `${s.disque_seuil_diametre_mm} mm : débité dans une tôle plutôt que dans une barre, ` +
          'où il partirait presque entièrement en copeaux.',
        alternatives: ['barre_ronde'],
      };
    }
    return {
      shape: 'barre_ronde',
      par_defaut: false,
      raison:
        `Disque de Ø${diametre} mm, sous le seuil d'atelier : débité dans une barre ronde, ` +
        'plus simple à tenir.',
      alternatives: ['barreau', 'barre_ronde_prismatique'],
    };
  }

  if (formePiece === 'prismatique') {
    return {
      shape: 'barreau',
      par_defaut: false,
      raison: 'Pièce prismatique : elle se débite dans un barreau.',
      alternatives: ['barre_ronde_prismatique'],
    };
  }

  // ── §3.4 — L'ABSENCE DE DÉCISION N'EXISTE PLUS ──
  //
  // Cette branche rendait `shape: null`, au motif qu'une forme choisie
  // au hasard produirait un volume faux. Le raisonnement était juste sur
  // le hasard, et faux sur la conclusion : **le barreau n'est pas un
  // hasard, c'est l'enveloppe prismatique.**
  //
  // Elle est toujours calculable — l'encombrement suffit — et c'est la
  // plus NEUTRE : elle borne la pièce au plus juste sans supposer ni
  // révolution ni reprise. Un barreau marqué « à confirmer » est une
  // hypothèse qu'on voit et qu'on corrige en un clic ; un `null`
  // remontait jusqu'à l'écran en « prix indisponible », et c'est
  // exactement ce que le §0 interdit depuis le 5 août.
  //
  // Le bug d'origine reste le témoin de cette règle : sur une rondelle
  // Ø75 × 7, le modèle rendait « brut prismatique » puis ne pouvait
  // remplir ni longueur, ni largeur, ni épaisseur — trois cotes qui
  // n'existent pas sur un disque. On lui demandait ce qu'il ne pouvait
  // pas savoir. Ici c'est l'inverse : on ne demande rien, on propose la
  // seule forme qui ne suppose rien.
  //
  // ── SAUF QUAND UN Ø HORS-TOUT EST LU — règle de Thibaud, 6 octobre 2026 ──
  //
  // « Dès qu'un Ø est lu sur l'encombrement global de la pièce, le brut
  // part par défaut sur une barre ronde, jamais sur un barreau. » Le
  // barreau n'est neutre que pour une pièce sans Ø extérieur : autour
  // d'une bride Ø97, il fait disparaître le Ø de l'écran et demande une
  // « profondeur » qui n'existe pas (devis 2026-10-010).
  const diametreGlobal = Number(ctx.diametre_global_mm);
  if (diametreGlobal > 0) {
    return horsAxe
      ? {
          shape: 'barre_ronde_prismatique',
          par_defaut: true,
          raison:
            `Ø hors-tout ${diametreGlobal} mm lu, avec des entités hors axe : barre ronde puis `
            + 'reprise sur un poste prismatique. Forme par défaut, à confirmer.',
          alternatives: ['barre_ronde', 'barreau'],
        }
      : {
          shape: 'barre_ronde',
          par_defaut: true,
          raison: `Ø hors-tout ${diametreGlobal} mm lu : barre ronde par défaut, à confirmer.`,
          alternatives: ['barre_ronde_prismatique', 'barreau'],
        };
  }
  return {
    shape: 'barreau',
    par_defaut: true,
    raison:
      'Forme de la pièce non tranchée : barreau proposé par défaut — c’est l’enveloppe '
      + 'prismatique, toujours calculable et la plus neutre. Forme par défaut, à confirmer.',
    alternatives: BRUT_SHAPES.filter((f) => f !== 'barreau'),
  };
}

/**
 * Cotes du brut = encombrement + surépaisseur d'usinage.
 *
 * @returns {{shape, diametre_mm, longueur_mm, largeur_mm, epaisseur_mm, detail}}
 */
/**
 * Une cote de SECTION, majorée puis arrondie À LA DIZAINE SUPÉRIEURE.
 *
 * ── LE CATALOGUE DE TAILLES COMMERCIALES A ÉTÉ SUPPRIMÉ — 25 août 2026 ──
 *
 * Charles : « le brut ne doit jamais se faire sur un catalogue. C'est
 * simplement la vraie forme de la pièce + une marge + un arrondi à la
 * dizaine supérieure. Comme ça ça fonctionnera avec tout, à chaque
 * fois. »
 *
 * Il a raison, et le catalogue avait un défaut qu'aucun réglage ne
 * corrige : **il refuse quand il ne couvre pas**. Sur le devis
 * 2026-08-055, un bloc d'acier de 140 × 110 × 112 — un plan parfaitement
 * ordinaire — sortait sans brut, sans masse et sans prix, parce que la
 * liste des épaisseurs acier plafonnait à 100 mm. Le chiffrage
 * dépendait de l'exhaustivité d'une table que personne ne tient à jour.
 *
 * ── CE QU'ON PERD, ET POURQUOI ÇA NE COMPTE PAS ──
 *
 * L'argument d'origine était « on achète ce qui se vend » : +2 mm au
 * rayon sur un Ø75 donne Ø79, et personne ne vend du Ø79. Il reste vrai,
 * et **l'arrondi à la dizaine y répond aussi** : Ø79 devient Ø80. Le
 * défaut d'AGC que la règle visait — une majoration en POURCENTAGE qui
 * produisait des épaisseurs comme 12,1 mm — reste impossible : une
 * dizaine n'est jamais une cote inventée.
 *
 * Ce qu'on perd est la finesse : une pièce qui aurait tenu dans un Ø85
 * achètera du Ø90. La matière est HORS ENGAGEMENT depuis le 5 août — elle
 * n'engage pas Mertens et se reconfirme à la commande — donc on échange
 * une précision indicative contre un calcul qui aboutit toujours.
 *
 * Vérification sur le cas qui a motivé la décision : 112 + 2 × 1 = 114
 * → **120**, exactement ce que la barre carrée du catalogue aurait donné.
 */
function monterCote(mini) {
  return dizaineSuperieure(mini);
}

export function dizaineSuperieure(cote) {
  const v = Number(cote);
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.ceil(v / 10) * 10;
}

export function cotesBrut(shape, encombrement = {}, settings = {}) {
  const s = { ...SUREPAISSEURS, ...settings };
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const r2 = (v) => Math.round(v * 100) / 100;

  /**
   * Une cote de section : majorée, puis arrondie à la dizaine
   * supérieure. Toujours vers le haut — un brut trop petit ne se
   * rattrape pas à l'atelier, un brut un peu large se débite.
   *
   * Elle ne peut plus RIEN refuser : c'est tout l'objet de la suppression
   * du catalogue (voir `monterCote`). Le seul refus qui subsiste dans ce
   * module est celui d'une cote ABSENTE — et il porte sur la lecture, pas
   * sur un approvisionnement.
   */
  const monter = (mini) => ({ valeur: dizaineSuperieure(mini) });

  const refus = () => ({
    shape,
    diametre_mm: null, longueur_mm: null, largeur_mm: null, epaisseur_mm: null,
    detail: null, manque_cote: null,
  });

  /**
   * ── UNE COTE ABSENTE NE VAUT PAS ZÉRO ──
   *
   * `n()` rend 0 sur `null`. Une épaisseur non cotée devenait donc
   * `0 + 2 × 2 = 4 mm`, arrondi à la dizaine supérieure : **10 mm**. Sur
   * une pièce sans aucune cote, le brut sortait à 10 × 10 × 10 — un
   * centimètre cube, avec un texte d'explication parfaitement normal
   * (« Épaisseur 0 + 2 × 2 = 4 mm »), un volume, une masse et un prix.
   *
   * Constaté le 21 août 2026 sur « Bloc à souder » (devis 2026-08-048) :
   * épaisseur non cotée sur le plan, brut 310 × 60 × **10** entièrement
   * fabriqué.
   *
   * C'est l'invariant 5 mot pour mot — *un zéro converti est une absence
   * déguisée* — et l'invariant 12 : **une fonction de conversion est un
   * lieu de naissance.** `n()` en est une, et personne n'avait décidé ce
   * que l'absence devenait.
   *
   * Elle devient un REFUS, dans le canal qui existe déjà pour ça. La
   * pièce sort « à compléter » en nommant la cote qui manque, au lieu de
   * sortir chiffrée sur un brut inventé.
   */
  const manqueCote = (cotes) => {
    const absentes = cotes.filter(([, v]) => !(n(v) > 0)).map(([nom]) => nom);
    if (absentes.length === 0) return null;
    // « a, b et c » — pas « a et b et c ». Et AUCUN « ou » : un message
    // qui propose deux causes n'en désigne aucune (§C, garde-fou C.1).
    const liste = absentes.length > 1
      ? `${absentes.slice(0, -1).join(', ')} et ${absentes[absentes.length - 1]}`
      : absentes[0];
    return {
      cotes: absentes, forme: shape,
      message:
        `Brut incalculable : ${liste} de la pièce ${
          absentes.length > 1 ? 'ne sont pas connues' : "n'est pas connue"
        }. Saisissez la cote manquante — un brut calculé sur une cote absente `
        + 'vaudrait 10 mm et produirait un prix.',
    };
  };

  const refusCote = (m) => ({ ...refus(null), manque_cote: m });

  /**
   * ── LES TROIS COTES SONT ORDONNÉES ──
   *
   * Rien ne garantissait laquelle des trois était l'épaisseur. La MÊME
   * pièce décrite « L500 l102 E20 » ou « L500 l20 E102 » donnait deux
   * bruts différents — et dans le second cas le programme cherchait une
   * **épaisseur** de 110 mm là où la section fait 20.
   *
   * La convention d'achat tranche, et elle est celle de l'atelier : on
   * commande un plat de SECTION largeur × épaisseur et on en DÉBITE une
   * longueur. La longueur est donc la plus grande cote, l'épaisseur la
   * plus petite. Le volume ne change pas ; ce qui change est qu'on
   * majore la bonne cote, et que l'écran dit
   * toujours la même chose de la même pièce.
   */
  const ordonner = (a, b, c) => {
    const [grand, moyen, petit] = [n(a), n(b), n(c)].sort((x, y) => y - x);
    return { longueur: grand, largeur: moyen, epaisseur: petit };
  };

  if (shape === 'barre_ronde' || shape === 'barre_ronde_prismatique') {
    // Une pièce prismatique tirée d'un rond doit TENIR dans le rond :
    // c'est la DIAGONALE de la section qui commande, pas le plus grand
    // côté. Prendre le côté ferait acheter une barre où la pièce
    // n'entre pas, et ça ne se voit qu'à l'atelier, au débit.
    // ── §3.1 : toute forme est calculable PAR BORNAGE ──
    //
    // Le bornage ne s'appliquait qu'à `barre_ronde_prismatique`. Sur une
    // pièce prismatique 100 × 50 × 20, `barre_ronde` lisait un
    // `diametre_mm` absent, donc 0, et rendait **Ø4 mm** — le double de
    // la surépaisseur, et rien d'autre.
    //
    // Un Ø4 est un nombre parfaitement valide : il ne déclenche aucune
    // validation, il s'affiche, il produit un volume et un prix. C'est
    // la même famille que le zéro de `num(null)` — le canal est bon, le
    // contenu ment. Et une des quatre variantes précalculées aurait été
    // fausse d'un facteur mille sur la masse.
    //
    // Le bornage vaut pour LES DEUX formes rondes : une pièce
    // prismatique tirée d'un rond doit tenir dans le rond, et c'est la
    // DIAGONALE de sa section qui commande, jamais son plus grand côté.
    // Même règle que le barreau : sans cote, pas de bornage. Un Ø absent
    // ET une section absente donnaient `Math.hypot(0, 0) = 0`, donc un
    // Ø4 — le double de la surépaisseur, et rien d'autre.
    const rond = n(encombrement.diametre_mm);
    const manqueRond = rond > 0
      ? manqueCote([["l'épaisseur", n(encombrement.longueur_mm) || n(encombrement.epaisseur_mm)]])
      : manqueCote([
        ['la longueur', encombrement.longueur_mm],
        ['la largeur', encombrement.largeur_mm],
      ]);
    if (manqueRond) return refusCote(manqueRond);

    const surMesure =
      rond
      || Math.hypot(n(encombrement.longueur_mm), n(encombrement.largeur_mm));

    const brutMini = surMesure + 2 * s.tournage_rayon_mm;
    const monte = monter(brutMini);
    const diametre = monte.valeur;

    // Sur une pièce de révolution, l'axe porte l'épaisseur (une rondelle)
    // ou la longueur (un arbre). Sur une pièce prismatique bornée dans
    // un rond, c'est l'épaisseur qui devient la longueur débitée.
    //
    // `n(a ?? b)` ne retombait PAS sur `b` quand `a` valait 0 : `??` ne
    // teste que `null`/`undefined`, et `0` est présent. Le `||` qui
    // suivait rattrapait le cas, mais l'écriture disait autre chose que
    // ce qu'elle faisait.
    const brut = n(encombrement.longueur_mm) || n(encombrement.epaisseur_mm);
    const longueur = r2(brut + 2 * s.dressage_face_mm + s.trait_de_scie_mm);

    const origine = n(encombrement.diametre_mm)
      ? `Ø pièce ${r2(surMesure)} mm`
      : `diagonale de la section ${r2(surMesure)} mm — la pièce doit tenir dans le rond`;

    return {
      shape,
      diametre_mm: diametre,
      longueur_mm: longueur,
      largeur_mm: null,
      epaisseur_mm: null,
      detail:
        `${origine} + 2 × ${s.tournage_rayon_mm} mm de surépaisseur = ${r2(brutMini)} mm` +
        ` → arrondi à la dizaine supérieure : Ø${diametre}. ` +
        `Longueur ${r2(brut)} + 2 × ${s.dressage_face_mm} de dressage + ` +
        `${s.trait_de_scie_mm} de trait de scie = ${longueur} mm.`,
      manque_cote: null,
    };
  }

  if (shape === 'barreau') {
    // Une pièce RONDE forcée en parallélépipède prend son Ø pour
    // longueur ET largeur : c'est le carré circonscrit.
    //
    // ── UNE PIÈCE RONDE SE RECONNAÎT À SON Ø, PAS À L'ABSENCE DE LONGUEUR ──
    //
    // Le test était « pas de longueur ET un Ø ». Or une pièce de
    // révolution arrive avec `longueur_mm` = sa longueur AXIALE
    // (`cotesDeLaPiece`) : dès que celle-ci était connue, la pièce
    // cessait d'être ronde et le barreau réclamait « la largeur et
    // l'épaisseur » — deux cotes qui n'existent pas sur un rond. Mesuré
    // sur 2026-10-027 (Ø41 × 15,3), le 22 octobre 2026 : barreau et « à
    // la cote » incalculables, alors que tout était connu.
    const estRonde = n(encombrement.diametre_mm) > 0 && !n(encombrement.largeur_mm);
    const axiale = n(encombrement.epaisseur_mm) || n(encombrement.longueur_mm);

    // Ce qu'il FAUT pour calculer, et rien de plus : une pièce ronde
    // reprise en barreau se décrit par son Ø et sa longueur axiale, qui
    // devient l'épaisseur du barreau ; une pièce prismatique par ses
    // trois cotes.
    const absentes = estRonde
      ? manqueCote([['le Ø', encombrement.diametre_mm], ["l'épaisseur", axiale]])
      : manqueCote([
        ['la longueur', encombrement.longueur_mm],
        ['la largeur', encombrement.largeur_mm],
        ["l'épaisseur", encombrement.epaisseur_mm],
      ]);
    if (absentes) return refusCote(absentes);

    // Les trois cotes sont ORDONNÉES avant d'être nommées : la longueur
    // est celle qu'on débite, l'épaisseur celle qu'on commande.
    const ordre = estRonde
      ? { longueur: n(encombrement.diametre_mm),
        largeur: n(encombrement.diametre_mm),
        epaisseur: axiale }
      : ordonner(encombrement.longueur_mm, encombrement.largeur_mm, encombrement.epaisseur_mm);
    const baseL = ordre.longueur;
    const baseW = ordre.largeur;

    // ── L et W montent AUSSI, et c'est un arbitrage explicite ──
    //
    // La règle historique disait « la longueur se débite, elle ne
    // s'arrondit pas » — vraie pour la longueur d'une BARRE, qu'on
    // tronçonne au trait de scie près. Elle ne l'est pas pour un
    // barreau : on découpe un rectangle dans une plaque, et une plaque
    // se commande en formats.
    //
    // Surtout, le §3.1 est explicite : « jamais de 79 × 79 × 11 ». Une
    // cote calculée ne s'affiche pas telle quelle, quelle qu'elle soit.
    const l = monterCote(baseL + 2 * s.fraisage_face_mm);
    const w = monterCote(baseW + 2 * s.fraisage_face_mm);

    const epaisseurMini = ordre.epaisseur + 2 * s.fraisage_face_mm;
    const monte = monter(epaisseurMini);
    const h = monte.valeur;

    return {
      shape,
      diametre_mm: null,
      longueur_mm: l,
      largeur_mm: w,
      epaisseur_mm: h,
      detail:
        `Longueur et largeur + 2 × ${s.fraisage_face_mm} mm par face, montées à la cote ` +
        `supérieure → ${l} × ${w} mm. ` +
        `Épaisseur ${r2(ordre.epaisseur)} + 2 × ${s.fraisage_face_mm} = ` +
        `${r2(epaisseurMini)} mm` +
        ` → arrondi à la dizaine supérieure : ${h} mm.` +
        (estRonde ? ' Pièce ronde reprise en parallélépipède : le Ø donne longueur et largeur.' : ''),
      manque_cote: null,
    };
  }

  if (shape === 'a_la_cote') {
    // ── La quatrième forme : AUCUNE surépaisseur ──
    //
    // « Tu vas recevoir une plaque ébauchée à 5 mm », « de l'étiré
    // 40×10 ». Les faces sont déjà bonnes : ajouter 2 mm par face ferait
    // payer une matière que personne n'achète, et surtout inventerait
    // des copeaux qui n'existent pas.
    //
    // Aucune montée en taille commerciale non plus, et c'est la même
    // raison : on ne CHOISIT pas ce brut, on le REÇOIT. Le monter au
    // standard suivant reviendrait à corriger une cote qui est un fait.
    // Rond = un Ø sans largeur, comme le barreau (22 octobre 2026).
    const estRonde = n(encombrement.diametre_mm) > 0 && !n(encombrement.largeur_mm);

    if (estRonde) {
      const manqueEp = manqueCote([
        ["l'épaisseur", n(encombrement.epaisseur_mm) || n(encombrement.longueur_mm)],
      ]);
      if (manqueEp) return refusCote(manqueEp);
      return {
        shape,
        diametre_mm: r2(n(encombrement.diametre_mm)),
        longueur_mm: r2(n(encombrement.epaisseur_mm) || n(encombrement.longueur_mm)),
        largeur_mm: null,
        epaisseur_mm: null,
        detail:
          'Brut reçu à la cote : aucune surépaisseur, aucune montée en taille commerciale. '
          + 'Les cotes du brut sont celles de la pièce finie.',
        manque_cote: null,
      };
    }

    const manqueACote = manqueCote([
      ['la longueur', encombrement.longueur_mm],
      ['la largeur', encombrement.largeur_mm],
      ["l'épaisseur", encombrement.epaisseur_mm],
    ]);
    if (manqueACote) return refusCote(manqueACote);

    // Ordonné comme le barreau : le brut reçu se décrit dans le même
    // vocabulaire que le brut acheté, sinon deux écrans nomment
    // différemment la même cote.
    const ordreCote = ordonner(
      encombrement.longueur_mm, encombrement.largeur_mm, encombrement.epaisseur_mm,
    );

    return {
      shape,
      diametre_mm: null,
      longueur_mm: r2(ordreCote.longueur),
      largeur_mm: r2(ordreCote.largeur),
      epaisseur_mm: r2(ordreCote.epaisseur),
      detail:
        'Brut reçu à la cote : aucune surépaisseur, aucune montée en taille commerciale. '
        + 'Les cotes du brut sont celles de la pièce finie — cochez les faces qui restent '
        + 'à usiner si ce n’est pas le cas.',
      manque_cote: null,
    };
  }

  return {
    shape: null, diametre_mm: null, longueur_mm: null, largeur_mm: null,
    epaisseur_mm: null, detail: null,
    manque_cote: null,
  };
}

/**
 * L'enveloppe géométrique d'un jeu de cotes de brut, pour le volume.
 *
 * Les quatre formes se ramènent à deux enveloppes : cylindrique pour les
 * deux formes rondes, prismatique pour le barreau et le brut à la cote —
 * SAUF quand ce dernier porte un Ø, auquel cas il est cylindrique lui
 * aussi. C'est la seule table qui traduit une forme d'ACHAT en forme de
 * CALCUL, et elle vit ici plutôt qu'en trois endroits.
 */
export function enveloppeDeCotes(cotes) {
  if (!cotes) return null;
  const rond = FORMES_RONDES.includes(cotes.shape) || cotes.diametre_mm != null;
  return rond
    ? { shape: 'cylinder', diameter: cotes.diametre_mm, length: cotes.longueur_mm }
    : { shape: 'box', l: cotes.longueur_mm, w: cotes.largeur_mm, h: cotes.epaisseur_mm };
}

/**
 * Les QUATRE variantes de brut, précalculées — §3.3.
 *
 * ── Pourquoi les quatre, et pas seulement celle qu'on propose ──
 *
 * Le §3.5 demande que basculer de forme soit INSTANTANÉ : le deviseur
 * clique, le volume, la masse, les copeaux et le prix suivent, sans
 * aller-retour serveur. Ça n'est possible que si les quatre sont déjà
 * là. Elles sont stockées sur la pièce (`pieces.brut_variants`), donc
 * un devis rouvert dans six mois les retrouve sans recalcul.
 *
 * ── Ce qui ne change PAS d'une forme à l'autre ──
 *
 * **Le volume FINI.** C'est la même pièce, quelle que soit la barre d'où
 * elle sort. Seul le BRUT change, donc les copeaux — et c'est
 * exactement ce que le ratio d'enlèvement rend visible : sur une
 * rondelle, choisir un barreau au lieu d'une barre ronde peut doubler
 * les copeaux sans changer d'un gramme ce que le client reçoit.
 *
 * Une variante qu'aucune taille commerciale ne couvre est rendue avec
 * son `manque_cote` et des volumes à `null` — jamais à zéro. C'est
 * l'invariant 5 : un « — » n'est pas un zéro.
 *
 * @param {object} encombrement  cotes de la pièce FINIE
 * @param {object} [settings]    surépaisseurs
 * @param {number|null} [volumeFiniCm3]  pour dériver les copeaux
 * @returns {Object<string, object>} une entrée par forme de `BRUT_SHAPES`
 */
export function variantesBrut(encombrement, settings = {}, volumeFiniCm3 = null) {
  const r2 = (v) => (v == null ? null : Math.round(v * 100) / 100);
  const variantes = {};

  for (const shape of BRUT_SHAPES) {
    const cotes = cotesBrut(shape, encombrement, settings);

    if (cotes.manque_cote || cotes.shape == null) {
      variantes[shape] = {
        shape,
        cotes: null,
        volume_brut_cm3: null,
        volume_copeaux_cm3: null,
        ratio_enlevement: null,
        detail: cotes.detail ?? null,
        manque_cote: cotes.manque_cote ?? null,
      };
      continue;
    }

    const volumeBrut = envelopeVolume(enveloppeDeCotes(cotes));
    const copeaux =
      volumeBrut != null && volumeFiniCm3 != null
        ? Math.max(0, volumeBrut - volumeFiniCm3)
        : null;

    variantes[shape] = {
      shape,
      cotes: {
        diametre_mm: cotes.diametre_mm,
        longueur_mm: cotes.longueur_mm,
        largeur_mm: cotes.largeur_mm,
        epaisseur_mm: cotes.epaisseur_mm,
      },
      volume_brut_cm3: r2(volumeBrut),
      volume_copeaux_cm3: r2(copeaux),
      // Le chiffre qui permet à l'œil de repérer un mauvais choix d'appro
      // en une seconde (§3.5).
      ratio_enlevement:
        volumeBrut > 0 && copeaux != null ? Math.round((copeaux / volumeBrut) * 1000) / 1000 : null,
      detail: cotes.detail,
      manque_cote: null,
    };
  }

  return variantes;
}
